import { getPlayers } from "@/api/publicRequests/players/getPlayers";
import { BottomSheet } from "@/design-system/BottomSheet/BottomSheet";
import { Tick } from "@/design-system/Check/Check";
import Input from "@/design-system/Input/Input";
import { ItemScoreDiamond } from "@/design-system/ItemScore/ItemScoreDiamond";
import PlayerCardCondensed, {
  playerCardCondensedSize,
} from "@/design-system/PlayerCard/PlayerCardCondensed";
import { pitchCardPlaceholder } from "@/designSystemIcons";
import useDebounce from "@/hooks/useDebounce";
import { useScrollLock } from "@/hooks/useScrollLock";
import { cn } from "@/lib/utils";
import { SourceChip } from "@/pages/Sbcs/components/SourceChip";
import { MetaRatingsPlayer } from "@/redux/players/playersSlice";
import { getUserSelector } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import {
  rankClubCards,
  rankMarketCards,
  toStreamlineOption,
} from "@repo/shared/streamline";
import { StreamlinePlayerOption } from "@repo/shared/types";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

const MIN_SEARCH_LENGTH = 3;
const SKELETON_COUNT = 8;
/* The same card the rows draw; on touch devices the card shrinks to fit
   its column and never grows past this. */
const CARD_SCALE = 0.65;
/* PlayerCardCondensed's unscaled width. */
const CARD_BASE_WIDTH = 190;
/* Touch: three columns on a phone, four from this width up; gap-x-2. */
const FOUR_COLUMNS_FROM = 480;
const GRID_GAP = 8;
/** A card in the grid: what it is, and why it cannot be picked, if it cannot. */
type Shown = { option: StreamlinePlayerOption; reason?: string };

/**
 * The player search behind "Add players": the same name search Compare Players
 * uses, drawn as the solution's own cards with the source chip under each.
 * Until a name is typed, a signed-in user sees their own club, the cards
 * worth the most points first; without a club to show, the market's
 * highest-rated cards. Cards with no item score (anything but a
 * base card) and club cards the solution already holds are shown greyed and
 * cannot be picked.
 *
 * The sheet opens on the cards already added, selected and leading the grid.
 * Picking a card selects it where it stands; picking it again unselects it.
 * When the search next changes, the selected cards move to the front of the
 * grid in pick order and the results follow them. Closing the sheet is the
 * confirmation: it hands back every selected card.
 */
export const AddPlayerSheet = ({
  isOpen,
  onClose,
  added,
  heldEaPlayerIds,
}: {
  isOpen: boolean;
  /** Closing confirms: the cards selected when the sheet closed. */
  onClose: (picked: StreamlinePlayerOption[]) => void;
  /** The cards added so far; what the sheet opens on. */
  added: StreamlinePlayerOption[];
  /** The physical club cards on screen already, added or not. */
  heldEaPlayerIds: Set<number>;
}) => {
  const { t } = useTranslation("sbcs");
  const [searchTerm, setSearchTerm] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  /* The cards picked, in pick order. */
  const [selected, setSelected] = useState<StreamlinePlayerOption[]>([]);
  /* The selected cards as of the last search: what leads the grid. A pick
     does not move a card until the search changes under it. */
  const [pinned, setPinned] = useState<StreamlinePlayerOption[]>([]);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const { debouncedSearchTerm, errorMessage } = useDebounce(searchTerm, {
    minLength: MIN_SEARCH_LENGTH,
  });
  const [players, setPlayers] = useState<MetaRatingsPlayer[]>([]);
  const [loading, setLoading] = useState(false);
  const [browse, setBrowse] = useState<MetaRatingsPlayer[]>([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const { user } = useSelector(getUserSelector);
  const signedIn = Boolean(user.uuid);
  useScrollLock(isOpen);
  /* Touch: the same full-height sheet as the alternatives, and cards sized
     to a fixed column count. As Compare's mobile screen, the grid's live
     width drives the maths. */
  const isTouch = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const gridRef = useRef<HTMLDivElement>(null);
  const [gridWidth, setGridWidth] = useState(0);
  useLayoutEffect(() => {
    const node = gridRef.current;
    if (!node) return;
    const measure = () => setGridWidth(node.getBoundingClientRect().width);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [isOpen]);
  const columns = gridWidth >= FOUR_COLUMNS_FROM ? 4 : 3;
  const scale =
    isTouch && gridWidth > 0
      ? Math.min(
          CARD_SCALE,
          (gridWidth - GRID_GAP * (columns - 1)) / columns / CARD_BASE_WIDTH,
        )
      : CARD_SCALE;

  /* Each opening starts from the cards added so far, all selected. The ref
     is set too, so the search effect below snapshots them rather than the
     previous opening's selection. */
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setPlayers([]);
      return;
    }
    selectedRef.current = added;
    setSelected(added);
    setPinned(added);
  }, [isOpen, added]);

  /* What the grid shows before a name is typed, once per opening: the club,
     ranked by points then value, so the cards worth the most points lead. A
     user with no club to show (signed out, or nothing imported) gets the
     market's highest-rated page instead, cheapest first within a points value. */
  useEffect(() => {
    if (!isOpen) {
      setBrowse([]);
      return;
    }
    let cancelled = false;
    setBrowseLoading(true);
    const club = signedIn
      ? getPlayers({ page: 1, sort: "rating", myClub: true }).then(
          ({ players }) => players,
        )
      : Promise.resolve([]);
    club
      .then((players) =>
        players.length > 0
          ? rankClubCards(players)
          : getPlayers({ page: 1, sort: "rating" }).then(({ players }) =>
              rankMarketCards(players),
            ),
      )
      .then((players) => {
        if (!cancelled) setBrowse(players);
      })
      .finally(() => {
        if (!cancelled) setBrowseLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, signedIn]);

  useEffect(() => {
    setPinned(selectedRef.current);
    if (debouncedSearchTerm.length < MIN_SEARCH_LENGTH) {
      setPlayers([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getPlayers({ page: 1, sort: "rating", searchText: debouncedSearchTerm })
      .then(({ players }) => {
        if (!cancelled) setPlayers(players);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearchTerm]);

  const size = playerCardCondensedSize(scale);
  const searched = debouncedSearchTerm.length >= MIN_SEARCH_LENGTH;
  const showSkeletons = searched ? loading : browseLoading;
  const pinnedIds = new Set(pinned.map((p) => p.player.resourceId));
  const selectedIds = new Set(selected.map((p) => p.player.resourceId));

  /* The pinned cards lead, always pickable; the browse list stands in for
     the results until the search has something to say, and neither repeats a
     pinned card. */
  const results: Shown[] = (searched ? players : browse)
    .filter((player) => !pinnedIds.has(player.resourceId))
    .map((player) => {
      const held =
        player.owned &&
        player.eaPlayerId !== null &&
        heldEaPlayerIds.has(player.eaPlayerId);
      const reason =
        player.itemScore === undefined
          ? t("streamline.noItemScore")
          : held
            ? t("streamline.alreadyAdded")
            : undefined;
      return { option: toStreamlineOption(player), reason };
    });
  const shown: Shown[] = [
    ...pinned.map((option) => ({ option })),
    ...(showSkeletons ? [] : results),
  ];

  const onPick = (option: StreamlinePlayerOption) => {
    const id = option.player.resourceId;
    setSelected((prev) =>
      prev.some((p) => p.player.resourceId === id)
        ? prev.filter((p) => p.player.resourceId !== id)
        : [...prev, option],
    );
    /* Back to the search, so typing carries on. */
    inputRef.current?.focus();
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={() => onClose(selected)}
      className={
        isTouch
          ? "h-dvh max-w-full rounded-none bg-neutral-900"
          : "h-[80dvh] max-w-3xl bg-neutral-900"
      }
      /* The design system's header, as the alternatives sheet uses: the caret
         that drops the sheet (and confirms the picks) sits left of the title. */
      header={{
        title: t("streamline.addPlayers"),
        closeLabel: t("streamline.closeAddPlayer"),
      }}
    >
      {/* On the same rail as the header, so nothing runs to the edges. */}
      <div className="mx-auto w-full max-w-content px-4 pb-8 large:px-16 touch-device:px-4">
        <div className="w-full max-w-90">
          <Input
            inputRef={inputRef}
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t("streamline.searchPlaceholder")}
            type="text"
            size="small"
            autoFocus
          />
        </div>
        {errorMessage && (
          <p className="pt-2 text-sm text-dark-secondary">{errorMessage}</p>
        )}
        {!errorMessage && !loading && searched && players.length === 0 && (
          <p className="pt-2 text-sm text-dark-secondary">
            {t("common:noPlayersFound")}
          </p>
        )}
        <div
          ref={gridRef}
          className={cn(
            "pt-5",
            isTouch
              ? "grid justify-items-center gap-x-2 gap-y-4"
              : "flex flex-wrap gap-x-2.5 gap-y-4",
          )}
          style={
            isTouch
              ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }
              : undefined
          }
        >
          {shown.map(({ option, reason }) => {
            const { player } = option;
            const points = player.itemScore;
            const isSelected = selectedIds.has(player.resourceId);
            return (
              <button
                key={player.resourceId}
                type="button"
                disabled={reason !== undefined}
                title={reason}
                onClick={() => onPick(option)}
                aria-pressed={isSelected}
                aria-label={t(
                  isSelected ? "streamline.removeNamed" : "streamline.addNamed",
                  { name: player.cardName },
                )}
                className={cn(
                  "flex flex-col items-center gap-1",
                  reason ? "opacity-40" : "cursor-pointer",
                )}
              >
                <div
                  className={cn(
                    "relative rounded-lg",
                    isSelected && "ring-2 ring-streamline-success",
                  )}
                >
                  <PlayerCardCondensed
                    player={player}
                    position={option.position}
                    scale={scale}
                    untradeable={option.untradeable}
                  />
                  {isSelected && (
                    <span className="absolute -right-1.5 -top-1.5 flex size-5 items-center justify-center rounded-full bg-streamline-success text-streamline-on-success">
                      <Tick className="size-3" />
                    </span>
                  )}
                </div>
                <SourceChip source={option.source} small />
                {reason ? (
                  <span
                    style={{ maxWidth: size.width }}
                    className="text-center text-[10px] leading-tight text-neutral-400"
                  >
                    {reason}
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs font-extrabold text-streamline-points">
                    <ItemScoreDiamond size={11} />
                    {points?.toLocaleString()}
                  </span>
                )}
              </button>
            );
          })}
          {showSkeletons &&
            Array.from({ length: SKELETON_COUNT }, (_, card) => (
              <div
                key={card}
                style={{ ...size, animationDelay: `${card * 80}ms` }}
                className="flex shrink-0 items-center justify-center animate-pulse text-dark-disabled"
              >
                <div className="w-[88%]">{pitchCardPlaceholder}</div>
              </div>
            ))}
        </div>
      </div>
    </BottomSheet>
  );
};
